import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { add, format, formatDistance, differenceInMilliseconds, differenceInSeconds } from 'date-fns';
import { OnChange } from 'react-final-form-listeners';
import { Field, FormSpy } from 'react-final-form';

import { required } from '../../helpers/formValidators';
import {
  FormInputHolder,
  FormLabel,
  FormDateInput,
  FormTimeInput
} from '../form';

/**
 * Utility: Combines a date from one timestamp with the time from another.
 */
export const addDateAndTimeFields = (dateTimestamp, timeTimestamp) => {
  if (!dateTimestamp || !timeTimestamp) return new Date();
  try {
    const newDateTimestamp = format(new Date(dateTimestamp), "yyyy-MM-dd'T'");
    const newTimeTimestamp = format(new Date(timeTimestamp), 'HH:mm:ss.SSSxxx');
    return new Date(newDateTimestamp + newTimeTimestamp);
  } catch (e) {
    return new Date();
  }
};

/**
 * Utility: Returns a human-readable duration string.
 * This is the function that was causing your SyntaxError.
 */
export const getDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  try {
    return formatDistance(new Date(startTime), new Date(endTime), {
      includeSeconds: true
    });
  } catch (e) {
    return '';
  }
};

/**
 * Utility: Returns duration in milliseconds.
 */
export const getDurationStrict = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  try {
    return differenceInMilliseconds(new Date(endTime), new Date(startTime));
  } catch (e) {
    return 0;
  }
};

const Duration = () => {
  const { t } = useTranslation();

  const filterBeforeStart = useCallback((time, startTimeValue) => {
    if (!startTimeValue || !time) return false;
    try {
      const start = addDateAndTimeFields(new Date(), new Date(startTimeValue));
      const given = addDateAndTimeFields(new Date(), new Date(time));
      return start.getTime() < given.getTime();
    } catch (e) {
      return false;
    }
  }, []);

  return (
    <div className="duration-fields">
      <FormDateInput
        name="date"
        labelRenderer={() => (
          <FormLabel htmlFor="date">{t('tracker.date')}</FormLabel>
        )}
        validate={required}
        dateFormat="MMM, dd yyyy"
      />

      <FormInputHolder>
        <FormTimeInput
          name="startTime"
          labelRenderer={() => (
            <FormLabel htmlFor="startTime">{t('tracker.startTime')}</FormLabel>
          )}
          validate={required}
        />

        <Field name="endTime" subscription={{ value: true }}>
          {({ input: { value: endTimeValue, onChange: updateEndTime } }) => (
            <OnChange name="startTime">
              {(newStart, previousStart) => {
                if (!newStart || !previousStart || !endTimeValue) return;
                
                const diff = Math.abs(new Date(newStart).getTime() - new Date(previousStart).getTime());
                if (diff < 1000) return;

                try {
                  const durationInSec = differenceInSeconds(new Date(endTimeValue), new Date(previousStart));
                  if (!isNaN(durationInSec)) {
                    updateEndTime(add(new Date(newStart), { seconds: durationInSec }));
                  }
                } catch (error) {
                }
              }}
            </OnChange>
          )}
        </Field>

        <Field name="startTime" subscription={{ value: true }}>
          {({ input: { value: startTimeValue } }) => (
            <FormTimeInput
              name="endTime"
              labelRenderer={() => (
                <FormLabel htmlFor="endTime">{t('tracker.endTime')}</FormLabel>
              )}
              validate={required}
              filterTime={(time) => filterBeforeStart(time, startTimeValue)}
            />
          )}
        </Field>
      </FormInputHolder>

      <div className="total-time">
        <FormLabel>{t('tracker.totalTime', 'Total Time')}</FormLabel>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => {
            if (!values.startTime || !values.endTime) return <span>-</span>;
            return (
              <span>
                {getDuration(values.startTime, values.endTime)}
              </span>
            );
          }}
        </FormSpy>
      </div>
    </div>
  );
};

export default Duration;
